import { createFileRoute } from '@tanstack/react-router';
import { PageContainer } from '@/components/shared/PageContainer';
import { EmptyState } from '@/components/ui/empty-state';
import { useTranslator } from '@/hooks/useTranslator';
import { queryClient } from '@/lib/utils/queryClient';
import { DeleteUserDialog } from '../../-components/DeleteUserDialog';
import { NotificationPreferencesCard } from '../../-components/NotificationPreferencesCard';
import { RemoveUserCompanyDialog } from '../../-components/RemoveUserCompanyDialog';
import { UserCompaniesCard } from '../../-components/UserCompaniesCard';
import { UserDetailHeader } from '../../-components/UserDetailHeader';
import { UserInformationCard } from '../../-components/UserInformationCard';
import {
  adminUserQueryOptions,
  userCompaniesQueryOptions,
} from '../../-hooks/useAdminUser';
import { useAdminUserDetailPage } from '../../-hooks/useAdminUserDetailPage';

export const Route = createFileRoute('/management/users/(pages)/$id/')({
  loader: ({ params }) =>
    Promise.all([
      queryClient.ensureQueryData(adminUserQueryOptions(params.id)),
      queryClient.ensureQueryData(userCompaniesQueryOptions(params.id)),
    ]),
  component: ManagementUserDetailPage,
});

function ManagementUserDetailPage() {
  const { id } = Route.useParams();
  const page = useAdminUserDetailPage(id);
  const { getTranslation: t } = useTranslator({ page: 'managementUsers' });

  if (page.isError) {
    // Told apart from the branch below on purpose: a dropped request is not an
    // id that does not exist, and saying "this user does not exist" when the
    // network failed sends the reader looking for the wrong problem.
    return (
      <PageContainer>
        <EmptyState
          title={t('detail.loadFailed')}
          description={t('detail.loadFailedHint')}
        />
      </PageContainer>
    );
  }

  if (!page.user) {
    // The loader has already resolved by the time this renders, so a missing
    // user with no error means the id genuinely does not exist.
    return (
      <PageContainer>
        <EmptyState title={t('detail.notFound')} />
      </PageContainer>
    );
  }

  return (
    // Not `PageContainer`, following the CAPA detail pages: its padding would
    // sit *above* the sticky header inside the scrollport, so content scrolled
    // through that strip and the header clipped. Here the padding lives on the
    // sticky wrapper itself, which also carries `bg-background` so the full
    // width — gutters included — is opaque as rows pass behind it.
    //
    // The scroller is a plain block, not a flex column: flex children default
    // to `flex-shrink: 1` and would compress to fit the viewport instead of
    // overflowing it, which is what stopped this page scrolling at all.
    <div className='min-h-0 flex-1 overflow-auto @container'>
      <div className='sticky top-0 z-20 bg-background px-4 pt-3 pb-1 min-[1600px]:px-6 min-[1600px]:pt-4'>
        <UserDetailHeader
          user={page.user}
          onImpersonate={
            page.canImpersonate ? page.startImpersonation : undefined
          }
          isImpersonating={page.isImpersonating}
          onDelete={page.deletion.canDelete ? page.deletion.open : undefined}
        />
      </div>

      <div className='flex flex-col gap-4 px-4 pt-3 pb-3 min-[1600px]:px-6 min-[1600px]:pb-4'>
        <UserInformationCard user={page.user} fields={page.fields} />

        <UserCompaniesCard
          companies={page.companies}
          facilityProcessLabel={page.facilityProcessLabel}
          onSetPrimary={page.actions.setPrimary}
          onRemove={page.queueRemoval}
          onRemoveMany={page.queueRemovalMany}
          addForm={page.addForm}
          isAddOpen={page.isAddOpen}
          onAddOpenChange={page.setAddOpen}
          rowSelection={page.rowSelection}
          onRowSelectionChange={page.setRowSelection}
          isLoading={page.isLoadingCompanies}
          disabled={page.actions.isPending}
        />

        <NotificationPreferencesCard user={page.user} />
      </div>

      {/* Portalled, so they sit outside the scrolling stack. */}
      <RemoveUserCompanyDialog
        companies={page.pendingRemoval}
        onOpenChange={open => !open && page.clearRemoval()}
        onConfirm={() => void page.confirmRemoval()}
        isPending={page.actions.isPending}
      />

      <DeleteUserDialog userName={page.displayName} state={page.deletion} />
    </div>
  );
}
